// WbCustomersPage — /weighbridge/customers (staff + admin).
//
// Phase C3. WB-lensed customer list from GET /api/v1/wb/customer-settings:
// each row is a tenant customer merged with its weighbridge settings
// (account status, credit limit, on-hold flag). Server-side paginated and
// searchable by name / email / phone. Row click opens the per-customer WB
// settings page. "Add Customer to WB" links to the central customer create
// flow — WB settings rows are auto-created on first save, so there is no
// separate WB-customer creation step.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Users } from 'lucide-react';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Badge } from '../../../components/ui/Badge/Badge';
import { EmptyState } from '../../../components/ui/EmptyState/EmptyState';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import {
  useWbCustomers,
  type WbAccountStatus,
} from '../hooks/useWbCustomers';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './WbCustomersPage.module.css';

const PER_PAGE = 50;

function AccountStatusBadge({ status }: { status: WbAccountStatus }): JSX.Element {
  switch (status) {
    case 'on_hold':
      return <Badge variant="danger">On Hold</Badge>;
    case 'closed':
      return <Badge variant="muted">Closed</Badge>;
    case 'active':
    default:
      return <Badge variant="success">Active</Badge>;
  }
}

function formatCurrency(value: number | null): string {
  if (value === null) return '—';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(value);
}

export function WbCustomersPage(): JSX.Element {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  // Debounce the search input (300ms) and reset to page 1 on change.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [search]);

  const params = useMemo(
    () => ({
      page,
      per_page: PER_PAGE,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
    }),
    [page, debouncedSearch],
  );

  const customersQuery = useWbCustomers(params);
  const customers = customersQuery.data?.customers ?? [];
  const pagination = customersQuery.data?.pagination;
  const totalPages = pagination?.total_pages ?? 1;
  const hasNoSearch = debouncedSearch === '';

  return (
    <div className={styles.page}>
      <PageHeader
        title="Customers"
        subtitle="Weighbridge account settings per customer"
        actions={
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => navigate('/customers/new')}
          >
            Add Customer to WB
          </Button>
        }
      />

      <div className={styles.toolbar}>
        <input
          className={styles.search}
          type="search"
          placeholder="Search by name, email or phone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search customers"
        />
      </div>

      {customersQuery.isError ? (
        <p className={styles.error}>
          {extractErrorMessage(customersQuery.error)}
        </p>
      ) : null}

      <Card flush>
        {customersQuery.isLoading ? (
          <div className={styles.loadingRow}>
            <Spinner label="Loading customers" />
          </div>
        ) : customers.length === 0 ? (
          hasNoSearch ? (
            <EmptyState
              icon={Users}
              title="No customers yet"
              description="Add a customer to start configuring weighbridge account settings."
              action={
                <Button
                  variant="primary"
                  icon={Plus}
                  onClick={() => navigate('/customers/new')}
                >
                  Add Customer to WB
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={Users}
              title="No matching customers"
              description={`No customers match "${debouncedSearch}".`}
            />
          )
        ) : (
          <>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Account</th>
                  <th className={styles.numeric}>Credit limit</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {customers.map((c) => (
                  <tr
                    key={c.customer_enc_id}
                    onClick={() =>
                      navigate(
                        `/weighbridge/customers/${encodeURIComponent(c.customer_enc_id)}`,
                      )
                    }
                  >
                    <td>{c.name}</td>
                    <td>
                      {c.email ?? <span className={styles.muted}>—</span>}
                    </td>
                    <td>
                      {c.phone ?? <span className={styles.muted}>—</span>}
                    </td>
                    <td>
                      <AccountStatusBadge status={c.account_status} />
                    </td>
                    <td className={styles.numeric}>
                      {formatCurrency(c.credit_limit)}
                    </td>
                    <td className={styles.actionsCell}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(
                            `/weighbridge/customers/${encodeURIComponent(c.customer_enc_id)}`,
                          );
                        }}
                      >
                        View / Edit Settings
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {pagination && totalPages > 1 ? (
              <div className={styles.pagination}>
                <span>
                  Page {pagination.page} of {totalPages} ({pagination.total}{' '}
                  total)
                </span>
                <div className={styles.pageControls}>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </Card>
    </div>
  );
}
